import * as React from 'react';
import { Button, DatePicker, Form, InputNumber, Select, Switch, Typography } from 'antd';
import { DeleteOutlined, LeftOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { DeliverablePlatform, DeliverablePostType } from '../types';

export type DeliverableDraft = {
    id?: number;
    campaign_id?: number;
    creator_id?: number;
    platform: DeliverablePlatform;
    post_type: DeliverablePostType;
    quantity: number;
    completed?: number;
    cost_per_post?: number | null;
    review_by?: string | null;
    publish_by?: string | null;
    usage_rights?: boolean;
    boosted_cost?: number | null;
    boosted_on?: string | null;
    rate?: number | null;
};

const PLATFORMS = [
    { label: 'Instagram', value: 'ig' },
    { label: 'YouTube', value: 'yt' },
    { label: 'TikTok', value: 'tt' },
];
const MEDIA = [
    { label: 'Photo', value: 'image' },
    { label: 'Video', value: 'video' },
    { label: 'Story', value: 'story' },
    { label: 'Text', value: 'text' },
    { label: 'Stream', value: 'stream' },
];

const platformName = (p: string) => PLATFORMS.find((o) => o.value === p)?.label ?? p;
const mediaName = (p: string) => MEDIA.find((o) => o.value === p)?.label ?? p;

function money(n?: number | null): string {
    if (n == null) return '';
    return `€${n.toLocaleString()}`;
}

const blank = (): DeliverableDraft => ({
    platform: 'ig',
    post_type: 'image',
    quantity: 1,
    cost_per_post: null,
    usage_rights: false,
    boosted_cost: null,
    rate: null,
});

type Props = {
    items: DeliverableDraft[];
    saving?: boolean;
    /** Creator deliverables can move completed up and down. Campaign templates leave this off. */
    showProgress?: boolean;
    onProgress?: (index: number, d: DeliverableDraft, completed: number) => Promise<void> | void;
    /** +1 or -1. The parent applies it immediately so clicks can stack. */
    onStep?: (index: number, d: DeliverableDraft, delta: number) => void;
    onCreate: (d: DeliverableDraft) => Promise<void> | void;
    onUpdate: (index: number, d: DeliverableDraft) => Promise<void> | void;
    onDelete: (index: number, d: DeliverableDraft) => Promise<void> | void;
};

const DeliverableManager: React.FC<Props> = ({ items, saving, showProgress, onProgress, onStep, onCreate, onUpdate, onDelete }) => {
    const [editing, setEditing] = React.useState<DeliverableDraft | null>(null);
    const [editIndex, setEditIndex] = React.useState<number | null>(null);

    const openNew = () => {
        setEditIndex(null);
        setEditing(blank());
    };
    const openEdit = (index: number, d: DeliverableDraft) => {
        setEditIndex(index);
        setEditing({ ...d });
    };

    const isNew = editIndex == null;
    const fee = ((editing?.cost_per_post ?? 0) * (editing?.quantity ?? 0)) + (editing?.boosted_cost ?? 0);

    const save = async () => {
        if (!editing) return;
        if (isNew) await onCreate(editing);
        else if (editIndex != null) await onUpdate(editIndex, editing);
        setEditing(null);
        setEditIndex(null);
    };

    if (editing) {
        const set = (patch: Partial<DeliverableDraft>) => setEditing({ ...editing, ...patch });
        return (
            <div>
                <Button type="text" icon={<LeftOutlined />} onClick={() => setEditing(null)} style={{ marginBottom: 8, paddingLeft: 0 }}>
                    All deliverables
                </Button>
                <Typography.Title level={4} style={{ marginTop: 0 }}>
                    {isNew ? 'New deliverable' : `Edit ${platformName(editing.platform)} deliverable`}
                </Typography.Title>
                <Form layout="horizontal" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} colon={false} requiredMark={false}>
                    <Form.Item label="Platform">
                        <Select value={editing.platform} options={PLATFORMS} onChange={(v) => set({ platform: v })} />
                    </Form.Item>
                    <Form.Item label="Media type">
                        <Select value={editing.post_type} options={MEDIA} onChange={(v) => set({ post_type: v })} />
                    </Form.Item>
                    <Form.Item label="Cost per post">
                        <InputNumber
                            style={{ width: '100%' }}
                            prefix="€"
                            min={0}
                            value={editing.cost_per_post ?? undefined}
                            onChange={(v) => set({ cost_per_post: v ?? null })}
                        />
                    </Form.Item>
                    <Form.Item label="Number of posts">
                        <InputNumber
                            style={{ width: 160 }}
                            min={1}
                            max={99}
                            value={editing.quantity}
                            onChange={(v) => set({ quantity: v ?? 1 })}
                        />
                    </Form.Item>
                    {showProgress && (
                        <Form.Item label="Completed">
                            <InputNumber
                                style={{ width: 160 }}
                                min={0}
                                max={editing.quantity}
                                value={editing.completed ?? 0}
                                onChange={(v) => set({ completed: v ?? 0 })}
                            />
                        </Form.Item>
                    )}
                    <Typography.Text type="secondary" style={{ display: 'block', margin: '8px 0 12px' }}>Other settings</Typography.Text>
                    <Form.Item label="Review by">
                        <DatePicker
                            style={{ width: '100%' }}
                            value={editing.review_by ? dayjs(editing.review_by) : null}
                            onChange={(d) => set({ review_by: d ? d.toISOString() : null })}
                        />
                    </Form.Item>
                    <Form.Item label="Publish by">
                        <DatePicker
                            style={{ width: '100%' }}
                            value={editing.publish_by ? dayjs(editing.publish_by) : null}
                            onChange={(d) => set({ publish_by: d ? d.toISOString() : null })}
                        />
                    </Form.Item>
                    <Form.Item label="Usage rights">
                        <Switch checked={!!editing.usage_rights} onChange={(v) => set({ usage_rights: v })} />
                    </Form.Item>
                    <Form.Item label="Boosted cost">
                        <InputNumber
                            style={{ width: '100%' }}
                            prefix="€"
                            min={0}
                            value={editing.boosted_cost ?? undefined}
                            onChange={(v) => set({ boosted_cost: v ?? null })}
                        />
                    </Form.Item>
                    <Form.Item label="Boosted on">
                        <DatePicker
                            style={{ width: '100%' }}
                            value={editing.boosted_on ? dayjs(editing.boosted_on) : null}
                            onChange={(d) => set({ boosted_on: d ? d.toISOString() : null })}
                        />
                    </Form.Item>
                    <Form.Item label="Rate">
                        <InputNumber
                            style={{ width: '100%' }}
                            prefix="€"
                            min={0}
                            value={editing.rate ?? undefined}
                            onChange={(v) => set({ rate: v ?? null })}
                        />
                    </Form.Item>
                </Form>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                    <Typography.Text strong>Estimated fee {money(fee) || '€0'}</Typography.Text>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {!isNew && editIndex != null && (
                            <Button danger type="text" icon={<DeleteOutlined />} onClick={async () => { await onDelete(editIndex, editing); setEditing(null); setEditIndex(null); }}>
                                Delete
                            </Button>
                        )}
                        <Button onClick={() => setEditing(null)}>Cancel</Button>
                        <Button type="primary" loading={saving} onClick={save}>{isNew ? 'Create' : 'Save'}</Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            {items.length === 0 && (
                <Typography.Text type="secondary">No deliverables yet.</Typography.Text>
            )}
            {items.map((d, i) => (
                <div
                    key={d.id ?? `new-${i}`}
                    style={{
                        display: 'flex', width: '100%', alignItems: 'center', textAlign: 'left', background: '#fff',
                        borderBottom: '1px solid #f0f0f0', padding: '12px 4px', gap: 12,
                    }}
                >
                    <button
                        type="button"
                        onClick={() => openEdit(i, d)}
                        style={{
                            display: 'flex', flex: 1, textAlign: 'left', background: 'transparent',
                            border: 'none', padding: 0, cursor: 'pointer', alignItems: 'center', gap: 12,
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 16, fontWeight: 600 }}>{platformName(d.platform)}</div>
                            <div style={{ color: '#8c8c8c', fontSize: 14 }}>
                                {mediaName(d.post_type)}
                                {d.cost_per_post != null ? ` · ${money(d.cost_per_post)}` : ''}
                                {` · ${d.quantity} post${d.quantity === 1 ? '' : 's'}`}
                            </div>
                        </div>
                        <RightOutlined style={{ color: '#bfbfbf' }} />
                    </button>
                    {showProgress && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                            <Button
                                size="small"
                                disabled={(d.completed ?? 0) <= 0}
                                onClick={() => onStep ? onStep(i, d, -1) : onProgress?.(i, d, Math.max(0, (d.completed ?? 0) - 1))}
                            >
                                −
                            </Button>
                            <span style={{ minWidth: 36, textAlign: 'center', fontWeight: 600 }}>
                                {d.completed ?? 0}/{d.quantity}
                            </span>
                            <Button
                                size="small"
                                disabled={(d.completed ?? 0) >= d.quantity}
                                onClick={() => onStep ? onStep(i, d, 1) : onProgress?.(i, d, Math.min(d.quantity, (d.completed ?? 0) + 1))}
                            >
                                +
                            </Button>
                        </div>
                    )}
                </div>
            ))}
            <Button type="dashed" icon={<PlusOutlined />} onClick={openNew} style={{ marginTop: 16 }}>
                Add deliverable
            </Button>
        </div>
    );
};

export default DeliverableManager;
